/**
 * Hell, dunkel oder wie das System — das Erscheinungsbild der Anwendung
 * (HM-107, ADR 0030).
 *
 * **Der Zustand steht nicht in der Adresse**, aus demselben Grund wie die
 * Schiene der Seitenleiste (`sidebar-rail.ts`): Die Adresse sagt, *was zu sehen
 * ist*; ein dunkles Bild ist keine andere Ansicht. Ein verschickter Link trüge
 * sonst die Vorliebe des Absenders zum Empfänger.
 *
 * **Er steht aber auch nicht nur im Speicher.** Wer einmal dunkel gewählt hat,
 * will es beim nächsten Öffnen wieder — also `localStorage`, und in keiner
 * Datenbank: Er beschreibt keinen Nutzer, sondern einen Bildschirm; derselbe
 * Nutzer will am Telefon abends etwas anderes als am Arbeitsplatz.
 *
 * **Drei Wahlen, zwei Bilder.** „Automatik" folgt `prefers-color-scheme`, auch
 * im laufenden Betrieb; „Hell" und „Dunkel" gelten, bis jemand anders wählt.
 * Die Automatik ist die Vorgabe und wird als **Fehlen** des Schlüssels
 * hinterlegt, nicht als drittes Wort: So deutet das Vor-Zeichen-Skript
 * (`public/theme-init.js`) einen Rest aus einer früheren Fassung genauso wie
 * eine bewusste Automatik — als „das System entscheidet".
 *
 * **Gesetzt wird `data-theme` auf `<html>`.** Das ist der Vertrag mit dem
 * Design-System (`[data-theme="dark"]` in `theme.css`) und mit dem Diagramm,
 * das genau dieses Attribut beobachtet. Der **erste** Wert steht schon vor dem
 * ersten Bild — aus dem Skript, das denselben Schlüssel liest; `theme.test.ts`
 * hält beide Seiten zusammen. Ohne das blitzte beim Laden das helle Bild auf.
 *
 * **Jeder Zugriff auf die Ablage ist eingefasst.** In einem privaten Fenster
 * mancher Browser wirft schon das Lesen; dann gilt die Automatik, und eine
 * Wahl hält nur für diese Sitzung.
 */
import { useCallback, useEffect, useState } from "react";

export const THEME_KEY = "hm.theme";

/** Was gezeichnet wird. */
export type Theme = "light" | "dark";

/** Was gewählt ist — `system` heißt Automatik. */
export type ThemePreference = Theme | "system";

/**
 * Was in der Ablage steht, gedeutet.
 *
 * **Nur `"dark"` und `"light"` gelten.** Alles andere — nichts hinterlegt, ein
 * Rest aus einer früheren Fassung, von Hand hineingeschrieben — heißt
 * Automatik. Ein `Boolean(wert)` machte aus `"false"` ein Dunkel.
 */
export function storedPreference(value: string | null): ThemePreference {
  return value === "dark" || value === "light" ? value : "system";
}

/** Aus der Wahl und der Systemeinstellung das, was gilt. */
export function effectiveTheme(preference: ThemePreference, systemDark: boolean): Theme {
  return preference === "system" ? (systemDark ? "dark" : "light") : preference;
}

const MEDIA = "(prefers-color-scheme: dark)";

function readPreference(): ThemePreference {
  try {
    return storedPreference(window.localStorage.getItem(THEME_KEY));
  } catch {
    return "system";
  }
}

function writePreference(preference: ThemePreference): void {
  try {
    if (preference === "system") window.localStorage.removeItem(THEME_KEY);
    else window.localStorage.setItem(THEME_KEY, preference);
  } catch {
    /* Gemerkt wird sie dann nur für diese Sitzung. Das Bild wechselt trotzdem. */
  }
}

function systemPrefersDark(): boolean {
  return typeof window.matchMedia === "function" && window.matchMedia(MEDIA).matches;
}

/**
 * Die Wahl samt Setzer — und das Bild, das daraus folgt.
 *
 * Das Attribut folgt dem Zustand im Effekt. Das Vor-Zeichen-Skript hat es
 * schon vor dem ersten Bild gesetzt, mit derselben Deutung; hier wird es nur
 * nachgeführt, wenn die Wahl oder das System wechselt.
 */
export function useTheme(): {
  preference: ThemePreference;
  theme: Theme;
  setPreference: (preference: ThemePreference) => void;
} {
  const [preference, setPreferenceState] = useState<ThemePreference>(() =>
    typeof window === "undefined" ? "system" : readPreference(),
  );
  const [systemDark, setSystemDark] = useState(() =>
    typeof window === "undefined" ? false : systemPrefersDark(),
  );

  /* Die Automatik folgt dem System auch im laufenden Betrieb. */
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return undefined;
    const query = window.matchMedia(MEDIA);
    const follow = (event: MediaQueryListEvent) => setSystemDark(event.matches);
    query.addEventListener("change", follow);
    return () => query.removeEventListener("change", follow);
  }, []);

  const theme = effectiveTheme(preference, systemDark);

  useEffect(() => {
    document.documentElement.dataset["theme"] = theme;
  }, [theme]);

  const setPreference = useCallback((next: ThemePreference) => {
    writePreference(next);
    setPreferenceState(next);
  }, []);

  return { preference, theme, setPreference };
}
